import { getFocusableSelector, nearestFocusableAncestor } from './isFocusable';

/**
 * A point the user clicked: the parts of the page it was on (the values of
 * the `anchorAttribute`s around it, innermost first) and, on text, the text
 * and the caret in it.
 */
export type PointHit = {
  x: number;
  y: number;
  anchors: string[];
  text: { content: string; offset: number } | null;
};

type CaretDocument = Document & {
  caretPositionFromPoint?(
    x: number,
    y: number
  ): { offsetNode: Node; offset: number } | null;
};

function textAt(element: Element, x: number, y: number): PointHit['text'] {
  // In a (read-only) text input the click itself put the caret.
  if (
    (element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement) &&
    element.selectionStart !== null
  ) {
    return { content: element.value, offset: element.selectionStart };
  }
  const doc: CaretDocument = document;
  const position = doc.caretPositionFromPoint
    ? doc.caretPositionFromPoint(x, y)
    : (() => {
        const range = document.caretRangeFromPoint?.(x, y);
        return range
          ? { offsetNode: range.startContainer, offset: range.startOffset }
          : null;
      })();
  return position?.offsetNode.nodeType === Node.TEXT_NODE
    ? {
        content: position.offsetNode.textContent ?? '',
        offset: position.offset
      }
    : null;
}

/** Reads what is under `x`, `y`; call before the DOM changes. */
export function readPointHit(
  x: number,
  y: number,
  anchorAttribute: string
): PointHit {
  const element = document.elementFromPoint?.(x, y) ?? null;
  const anchors: string[] = [];
  for (
    let part = element?.closest(`[${anchorAttribute}]`);
    part;
    part = part.parentElement?.closest(`[${anchorAttribute}]`)
  ) {
    anchors.push(part.getAttribute(anchorAttribute) ?? '');
  }
  return { x, y, anchors, text: element ? textAt(element, x, y) : null };
}

const TEXT_INPUT =
  'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]), textarea';

/** How a control takes a click passed on to it. */
type Take =
  | { type: 'type'; input: HTMLInputElement | HTMLTextAreaElement }
  | { type: 'open' }
  | { type: 'focus' };

/** `null`: the control is not used on the user's behalf. */
function takeOf(control: HTMLElement): Take | null {
  if (
    control instanceof HTMLInputElement ||
    control instanceof HTMLTextAreaElement
  ) {
    // A checkbox or radio would toggle.
    return control.matches(TEXT_INPUT)
      ? { type: 'type', input: control }
      : null;
  }
  if (
    control.hasAttribute('aria-haspopup') ||
    control.hasAttribute('aria-expanded')
  ) {
    return { type: 'open' };
  }
  // An action: remove, add, a link.
  if (control.matches('button, a[href], summary')) return null;
  return { type: 'focus' };
}

/**
 * Hands a click made before the DOM changed (a double click that swapped a
 * read-only view for its editor, remounting it) on to the same part now: the
 * innermost of the hit's anchors still there (a fee, else the rate, else the
 * field), and in it the control at the point, or else its first usable one.
 * A text input takes the caret where it was, a popup trigger (select, date
 * picker) opens, a label focuses its control; actions are never clicked on
 * the user's behalf. Without anchors only the control at the point counts.
 * `false` when nothing under `container` took it.
 */
export function activateControlAt(
  container: HTMLElement,
  hit: PointHit,
  anchorAttribute: string
): boolean {
  const anchored = Array.from(
    container.querySelectorAll(`[${anchorAttribute}]`)
  );
  const element = document.elementFromPoint?.(hit.x, hit.y);
  const pointed = element
    ? (element.closest('label')?.control ?? nearestFocusableAncestor(element))
    : null;
  // Per scope, innermost first: the input holding the clicked text, the
  // control at the point, then its own.
  const candidates =
    hit.anchors.length === 0
      ? [{ scope: container, control: pointed }]
      : hit.anchors.flatMap(anchor => {
          const scope = anchored.find(
            part => part.getAttribute(anchorAttribute) === anchor
          );
          if (!scope) return [];
          // The anchor may sit on the control itself (a part's input).
          const controls = [
            scope,
            ...scope.querySelectorAll(getFocusableSelector())
          ].filter(control => control.matches(getFocusableSelector()));
          // The input holding the clicked text beats whatever lies at the
          // point now (the amount's "450", not the currency beside it).
          const holdingText = controls.find(
            control =>
              hit.text !== null &&
              hit.text.content.trim() !== '' &&
              (control instanceof HTMLInputElement ||
                control instanceof HTMLTextAreaElement) &&
              control.value.includes(hit.text.content)
          );
          return [holdingText, pointed, ...controls].map(control => ({
            scope,
            control
          }));
        });
  for (const { scope, control } of candidates) {
    if (!(control instanceof HTMLElement) || !scope.contains(control)) continue;
    const take = takeOf(control);
    if (take === null) continue;
    control.focus();
    switch (take.type) {
      case 'type': {
        // The clicked text, found in the value: the caret goes where it was.
        const start = hit.text
          ? take.input.value.indexOf(hit.text.content)
          : -1;
        if (hit.text && start >= 0) {
          const caret = start + hit.text.offset;
          take.input.setSelectionRange(caret, caret);
        }
        break;
      }
      case 'open':
        control.click();
        break;
      case 'focus':
        break;
    }
    return true;
  }
  return false;
}
